import React, { useState } from 'react';
import { Confirmation, Card, IconButton, ActionDropdown } from 'components/common';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { deletePIF, setExpandAll, setCollapseTabStatus, resetExpandAll, downloadPIF, downloadPIFAsJSON } from "redux/crm/project/pifSlice";
import { Link } from 'react-router-dom';
import { FaBackward, FaDownload, FaFilePdf, FaEllipsisV, FaTrashAlt, FaAngleDoubleUp, FaAngleDoubleDown, FaArrowLeft, FaEye, FaEdit, FaSpinner } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { FaRegFileZipper } from 'react-icons/fa6';
import { LuHistory } from 'react-icons/lu';
import { VALUE_CLS } from './pifStyles';
import RestorePointsDrawer from './detail/RestorePointsDrawer';

const PIFHeader = ({pif, source, title, highlight, against, onRestored}) => {
  const [showConfirmationDelete, setShowConfirmationDelete] = useState(false);
  const [messageConfirm, setMessageConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {collapseTabStatus} = useSelector(state => state.crm_project_pif);
  const [loading, setLoading] = useState(false);
  const [showRestorePoints, setShowRestorePoints] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {project, building, adaptation} = pif || {};

  const projectId = project?.id;
  const buildingId = building?.id;
  const adaptationId = adaptation?.id;
  const pifId = pif?.id;
  const projectUrl = `/crm/projects/${projectId}`;
  const changeOrderUrl = `${projectUrl}/buildings/${buildingId}/change-orders/${adaptationId}`;
  const pifUrl = pif ? `${projectUrl}/buildings/${buildingId}/adaptations/${adaptationId}/pifs/${pifId}` : null;

  const {
    allow_edit,
    allow_delete,
    allow_restore,
    //allow_export_draft_proposal_contract
  } = pif?.permissions || {};

  const handlePreviewPIF = () => {
    navigate(`${pifUrl}/print`, { state: { pif } })
  }

  const handleEditPIF = () => {
    navigate(`${pifUrl}/edit`, { state: { pif } })
  }

  const handleProject = () => {
    navigate(projectUrl)
  }

  const confirmMsg = (message) => {
    setMessageConfirm(message)
    setShowConfirmationDelete(true)
  }

  const closeConfirmModal = () => {
    setShowConfirmationDelete(false)
  }

  const handleCloseConfirmation = () => {
    closeConfirmModal()
  }

  const handleActionConfirm = async () => {
    setDeleting(true)
    try {
      await dispatch(deletePIF({projectId, buildingId, adaptationId, pifId})).unwrap()

      closeConfirmModal()
      source === "change-order" ? navigate(changeOrderUrl) : navigate(projectUrl)
    }
    catch(error) {
      toast.error(error?.error || error?.message || 'Failed to delete PIF.')
    }
    finally {
      setDeleting(false)
    }
  }

  const handleDeletePIF = () => {
    confirmMsg(<>Are you sure to delete PIF ID#<strong className="tw-text-red-600">{pifId}</strong>?</>);
  }

  const handleExpandAll = () => {
    if (collapseTabStatus) {
      dispatch(setExpandAll())
    }
    else {
      dispatch(resetExpandAll())
    }

    dispatch(setCollapseTabStatus(!collapseTabStatus))
  }

  const handleDownloadPIF = async () => {
    try {
      setLoading(true)
      await dispatch(downloadPIF({
        projectId,
        buildingId,
        adaptationId,
        pifId,
        highlight: !!highlight,
        compareWith: highlight ? against : undefined
      })).unwrap();
      toast.success('PIF downloaded as PDF successfully');
    }
    catch(error) {
      toast.error(error.message);
    }
    finally {
      setLoading(false)
    }
  }

  const handleDownloadPIFAsJSON = async () => {
    try {
      setLoading(true)
      await dispatch(downloadPIFAsJSON({
        projectId,
        buildingId,
        adaptationId,
        pifId
      })).unwrap();
      toast.success('PIF downloaded as JSON successfully');
    }
    catch(error) {
      toast.error(error.message);
    }
    finally {
      setLoading(false)
    }
  }

  const labelValueRow = (label, value) => (
    <div className="tw-grid tw-grid-cols-2 tw-gap-x-3">
      <div className="tw-text-right">{label} :</div>
      <div className={`${VALUE_CLS} tw-font-bold`}>{value}</div>
    </div>
  )

  return (
    <>
      <Card
        padding="none"
        className="tw-mb-3"
        header={
          <div className="tw-flex tw-items-center tw-justify-between">
            <IconButton
              onClick={() => navigate(-1)}
              size="sm"
              icon={<FaArrowLeft size={12} />}
            />
            <div className="tw-flex tw-items-center tw-gap-1">
              <IconButton
                size="sm"
                icon={collapseTabStatus ? <FaAngleDoubleDown size={12} /> : <FaAngleDoubleUp size={12} />}
                title="Collapse All"
                onClick={handleExpandAll}
              />

              <IconButton
                size="sm"
                icon={<FaEye size={12} />}
                title="Preview PIF"
                onClick={handlePreviewPIF}
              />

              <ActionDropdown
                trigger={<FaDownload size={12} />}
                align="end"
                menuWidth={200}
                items={[
                  {
                    label: loading ? 'Downloading...' : 'Download as PDF',
                    icon: <FaFilePdf size={12} className="tw-text-primary-600" />,
                    onClick: handleDownloadPIF,
                    disabled: loading
                  },
                  {
                    label: loading ? 'Downloading...' : 'Download as JSON',
                    icon: <FaRegFileZipper size={12} className="tw-text-amber-500" />,
                    onClick: handleDownloadPIFAsJSON,
                    disabled: loading
                  }
                ]}
              />

              {allow_edit && (
                <IconButton
                  size="sm"
                  icon={<FaEdit size={12} />}
                  onClick={handleEditPIF}
                >
                  <span className="tw-hidden xl:tw-inline-block">Edit</span>
                </IconButton>
              )}

              {/* Next to Edit because that is where a mistake gets made. Gated on
                  allow_restore, not allow_edit: a closed PIF stays editable by
                  CSD but can never be restored. */}
              {allow_restore && (
                <IconButton
                  size="sm"
                  icon={<LuHistory size={13} />}
                  onClick={() => setShowRestorePoints(true)}
                  title="Restore this PIF from an earlier version"
                >
                  <span className="tw-hidden xl:tw-inline-block">History</span>
                </IconButton>
              )}

              <IconButton
                size="sm"
                icon={<FaArrowLeft size={12} />}
                className="tw-hidden sm:tw-inline-flex"
                onClick={handleProject}
              >
                <span className="tw-hidden xl:tw-inline-block">Back to Project</span>
              </IconButton>

              {allow_delete && (
                <ActionDropdown
                  trigger={<FaEllipsisV size={12} />}
                  align="end"
                  items={[
                    {
                      label: 'Delete PIF',
                      icon: <FaTrashAlt size={12} />,
                      onClick: handleDeletePIF,
                      danger: true
                    }
                  ]}
                />
              )}
            </div>
          </div>
        }
      >
        {loading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-mt-2 tw-text-sm tw-text-slate-600">
            <FaSpinner className="tw-animate-spin tw-mr-2" /> Downloading...
          </div>
        )}

        <div className="tw-p-4 tw-text-sm">
          <h5 className="tw-text-center tw-mb-3 tw-font-bold tw-text-primary-600">{title} PROJECT INFORMATION FORM (PIF)</h5>
          <div className="tw-text-center tw-mb-2">ID#: <span className={`tw-font-bold ${VALUE_CLS}`}>{pif?.pif_number}</span></div>

          {labelValueRow('Project', project?.name)}
          {labelValueRow('Building', building?.name)}
          {labelValueRow('Quote no', <>{project?.q_number}<span> - {building?.j_number}</span></>)}

          {adaptation?.type == "change_order" ? (
            <div className="tw-grid tw-grid-cols-2 tw-gap-x-3">
              <div className="tw-text-right">CO# :</div>
              <div>
                <span className={`${VALUE_CLS} tw-font-bold tw-mr-3`}>{adaptation?.co}</span>
                <span>
                  <FaBackward className="tw-mr-1 tw-inline" />
                  <Link to={`/crm/projects/${project?.id}/buildings/${building?.id}/change-orders/${adaptation?.id}`}>Back to Change order</Link>
                </span>
              </div>
            </div>
          ) : (
            labelValueRow('REV#', adaptation?.rev)
          )}
        </div>
      </Card>

      <Confirmation
        show={showConfirmationDelete}
        type="danger"
        title="Delete PIF"
        message={messageConfirm}
        confirmText="Delete"
        loading={deleting}
        onConfirm={handleActionConfirm}
        onCancel={handleCloseConfirmation}
      />

      <RestorePointsDrawer
        show={showRestorePoints}
        onHide={() => setShowRestorePoints(false)}
        pif={pif}
        onRestored={onRestored}
      />
    </>
  );
};

PIFHeader.propTypes = {
  onRestored: PropTypes.func,
  project: PropTypes.object.isRequired,
  building: PropTypes.object.isRequired,
  adaptation: PropTypes.object.isRequired,
  pif: PropTypes.object.isRequired,
  source: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  highlight: PropTypes.bool,
  against: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default PIFHeader;
